<template>
  <div class="tpage">
    <div class="topbar">
      <el-button type="text" icon="el-icon-arrow-left" @click="$router.push('/tedu')">返回</el-button>
      <span class="title">登记记录</span>
      <el-button type="text" @click="$router.push('/tedu-register')">登记</el-button>
    </div>

    <div class="body">
      <div class="search-bar">
        <el-input v-model.trim="keyword" size="small" clearable placeholder="搜班级 / 教师 / 课程 / 说明"
                  @keyup.enter.native="search" @clear="search"></el-input>
        <el-button size="small" type="primary" :loading="loading" @click="search">搜索</el-button>
      </div>
      <div class="type-bar">
        <el-radio-group v-model="recType" size="small" @change="search">
          <el-radio-button label="">全部</el-radio-button>
          <el-radio-button label="class">查课登记</el-radio-button>
          <el-radio-button label="teacher">教师登记</el-radio-button>
        </el-radio-group>
      </div>

      <p class="copy-hint">点击记录右下角的「复制」按钮，可复制该条内容</p>

      <el-card class="rec-card" shadow="never" v-for="item in list" :key="item.id">
        <div class="rec-head">
          <span class="cls">{{ item.class_name || '—' }}</span>
          <span class="time">{{ timeText(item) }}</span>
          <span class="score" :class="{ minus: Number(item.score) < 0 }">{{ item.score }} 分</span>
        </div>
        <div class="rec-line">教师：{{ item.teacher }} <span class="dot">·</span> 课程：{{ item.course }}</div>
        <div class="rec-market">{{ item.market }}</div>
        <div class="rec-imgs" v-if="item.picture && item.picture.length">
          <el-image v-for="(p, pi) in item.picture" :key="pi" :src="imgUrl(p.image_url)"
                    :preview-src-list="previewList(item)" class="thumb" fit="cover"></el-image>
        </div>
        <div class="rec-foot">
          <span class="foot-info">{{ item.create_time }} <span v-if="item.upload_person">· {{ item.upload_person }}</span></span>
          <span class="foot-ops">
            <el-button type="text" icon="el-icon-document-copy" class="copy-btn" @click="copyRecord(item)">复制</el-button>
            <el-button type="text" icon="el-icon-edit" class="op-btn" @click="editRecord(item)">编辑</el-button>
            <el-button type="text" icon="el-icon-delete" class="del-btn" @click="delRecord(item)">删除</el-button>
          </span>
        </div>
      </el-card>

      <p class="empty" v-if="loaded && list.length === 0">还没有登记记录</p>

      <div class="more" v-if="hasMore">
        <el-button size="small" :loading="loading" @click="loadMore">加载更多</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { eduRegisterList, eduRegisterDelete, getTeacherLogin } from '@/api/teacher.js'

const IMG_BASE = 'https://digital-school-education.oss-cn-shanghai.aliyuncs.com/public/static/education_register_image/'
const PAGE_SIZE = 10

export default {
  name: 'TeacherEduRecord',
  data() {
    return {
      list: [],
      page: 1,
      keyword: '',
      recType: '', // ''=全部 class=查课登记 teacher=教师登记
      hasMore: true,
      loading: false,
      loaded: false
    }
  },
  mounted() {
    if (!getTeacherLogin().token) {
      this.$router.replace('/tlogin')
      return
    }
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      eduRegisterList(this.page, this.keyword, this.recType)
        .then((data) => {
          this.loading = false
          this.loaded = true
          const rows = Array.isArray(data) ? data : (data && data.data ? data.data : [])
          this.list = this.page === 1 ? rows : this.list.concat(rows)
          this.hasMore = rows.length >= PAGE_SIZE
        })
        .catch(() => {
          this.loading = false
          this.loaded = true
          this.$message.error('加载失败，请稍后重试')
        })
    },
    /** 搜索：重置到第 1 页 */
    search() {
      this.page = 1
      this.loaded = false
      this.list = []
      this.load()
    },
    /** 编辑：把整条记录暂存，跳独立编辑页回填 */
    editRecord(item) {
      try {
        sessionStorage.setItem('edu_edit_record', JSON.stringify(item))
      } catch (e) {
        // 存储不可用时忽略，编辑页会走接口兜底取详情
      }
      this.$router.push({ path: '/tedu-edit', query: { id: item.id } })
    },
    /** 删除：确认后调后端（返回纯文本 success/error） */
    delRecord(item) {
      this.$confirm('确定删除这条登记记录吗？删除后不可恢复。', '删除确认', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => eduRegisterDelete(item.id))
        .then((res) => {
          if (res === 'success') {
            this.$message.success('删除成功')
            // 当前页删空且不是第一页时，回退一页
            if (this.list.length === 1 && this.page > 1) {
              this.page -= 1
            }
            this.load()
          } else {
            this.$message.error('删除失败，请稍后重试')
          }
        })
        .catch(() => {})
    },
    loadMore() {
      this.page += 1
      this.load()
    },
    /** ---------- 复制记录 ---------- */
    /** 星期 + 节次；教师登记类型的记录没有这两项，显示「教师登记」 */
    timeText(item) {
      const ws = [item.week_name, item.section_name].filter(Boolean).join(' · ')
      return ws || '教师登记'
    },
    recordText(item) {
      const lines = []
      if (item.score > 0) lines.push('【表扬】' + '[玫瑰][玫瑰][玫瑰][强][强][强]')
      if (item.score < 0) lines.push('【通报】'+'⚠️⚠️⚠️')
      if (item.class_name) lines.push('班级：' + item.class_name)
      const ws = [item.week_name, item.section_name].filter(Boolean).join(' ')
      if (ws) lines.push(ws)
      lines.push('教师：' + (item.teacher || '') + '　课程：' + (item.course || ''))
      lines.push('分值：' + item.score)
      if (item.market) lines.push('情况说明：' + item.market)
      lines.push('')
      lines.push('教务处')
      if (item.create_time) lines.push('时间：' + item.create_time)
      return lines.join('\n')
    },
    copyRecord(item) {
      this.copyText(this.recordText(item))
    },
    copyText(text) {
      const done = () => this.$message.success('内容已复制')
      const fail = () => this.$message.error('复制失败，请重启浏览器后再试')
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done, fail))
      } else {
        this.fallbackCopy(text, done, fail)
      }
    },
    /** http 环境下 navigator.clipboard 不可用，退回 execCommand 方案 */
    fallbackCopy(text, done, fail) {
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        if (ok) {
          done()
        } else {
          fail()
        }
      } catch (e) {
        fail()
      }
    },
    imgUrl(name) {
      return IMG_BASE + name
    },
    previewList(item) {
      return (item.picture || []).map((p) => IMG_BASE + p.image_url)
    }
  }
}
</script>

<style scoped lang="scss">
.tpage {
  min-height: 100vh;
  background: #f4f6fb;
}
.topbar {
  background: #fff;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eef1f7;

  .title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2d3d;
  }
}
.body {
  padding: 12px;
}
.search-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;

  ::v-deep .el-input {
    flex: 1;
  }
}
.type-bar {
  margin-bottom: 10px;

  ::v-deep .el-radio-button__inner {
    padding: 7px 14px;
  }
}
.copy-hint {
  margin: 0 2px 10px;
  font-size: 12px;
  color: #a8b0c0;
}
.rec-card {
  border-radius: 10px;
  border: none;
  margin-bottom: 10px;

  ::v-deep .el-card__body {
    padding: 12px 14px;
  }
}
.rec-head {
  display: flex;
  align-items: center;
  gap: 8px;
  .cls {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
  }
  .time {
    font-size: 12px;
    color: #8a94a6;
    flex: 1;
  }
  .score {
    font-size: 14px;
    font-weight: 600;
    color: #2fa36b;
    &.minus {
      color: #e05252;
    }
  }
}
.rec-line {
  margin-top: 6px;
  font-size: 13px;
  color: #5b6577;
  .dot {
    color: #c8cede;
    margin: 0 4px;
  }
}
.rec-market {
  margin-top: 6px;
  font-size: 13px;
  color: #38445a;
  line-height: 1.6;
  word-break: break-all;
}
.rec-imgs {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  .thumb {
    width: 64px;
    height: 64px;
    border-radius: 6px;
  }
}
.rec-foot {
  margin-top: 8px;
  font-size: 12px;
  color: #a8b0c0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .foot-info {
    flex: 1;
    min-width: 0;
  }
  .foot-ops {
    white-space: nowrap;
    .op-btn {
      padding: 0;
      margin-left: 8px;
      font-size: 13px;
      color: #e6a23c;
    }
    .del-btn {
      padding: 0;
      margin-left: 8px;
      font-size: 13px;
      color: #e05252;
    }
  }
  .copy-btn {
    padding: 0;
    margin-left: 8px;
    font-size: 13px;
    color: #3b6ef0;
  }
}
.empty {
  text-align: center;
  color: #a8b0c0;
  font-size: 13px;
  margin-top: 40px;
}
.more {
  text-align: center;
  padding: 10px 0 24px;
}
</style>
